import React, {
  useCallback, useRef, useState, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col, Row, FormCheck, Button,
  Card,
} from 'react-bootstrap';
import RswEditor from 'react-simple-wysiwyg';
import { useForm, useFormState } from 'react-final-form';

import Select from 'components/Form/FormSelect';
import Datepicker from 'components/Form/FormDatepicker';
import TextField from 'components/Form/FormTextField';
import { InvitationSenders } from 'constants/utils';
import { TreeNode } from 'utils/documents';
import SearchContactModal from 'components/Modal/SearchContactModal';
import {
  ValidateDocumentoItem,
  ValidateDocumentsEstado,
  ValidateDocumentsResumen,
} from 'hooks/useValidateDocuments';
import DocumentTreeSelector from '../DocumentTreeSelector';
import EmailListManager from '../EmailListManager';
import ValidateDocumentsPanel from '../ValidateDocumentsPanel';

type ValidateDocumentsPanelConfig = {
  estado: ValidateDocumentsEstado;
  progreso: number;
  total: number;
  documentos: ValidateDocumentoItem[];
  resumen: ValidateDocumentsResumen | null;
  errorMessage: string | null;
  puedeEnviar: boolean;
  validacionId: string | null;
  onValidate: () => void;
  translateDocStatus: ( status: string ) => string;
};

type InvitationFormFieldsProps = {
  documentTree: TreeNode[];
  expandedDocuments: string[];
  futureInvestments: { id:string, name:string }[];
  setExpandedDocuments: ( expanded: string[] ) => void;
  html: string;
  onChangeEditor: ( e: { target: { value: string } } ) => void;
  validateDocuments?: ValidateDocumentsPanelConfig;
  onDocumentsSelectionChange?: () => void;
};

function InvitationFormFields( {
  documentTree,
  expandedDocuments,
  setExpandedDocuments,
  html,
  onChangeEditor,
  futureInvestments,
  validateDocuments,
  onDocumentsSelectionChange,
}: InvitationFormFieldsProps ) {
  const { t } = useTranslation();
  const form = useForm();
  const [showSearchModal, setSearchModal] = useState( false );
  const emailSearchType = useRef<'cc' | 'to'>();
  const {
    values, submitting, errors, submitFailed,
  } = useFormState( {
    subscription: {
      values: true, submitting: true, errors: true, submitFailed: true,
    },
  } );

  const isPersonal = ( values?.type ?? 'PERSONAL' ) === 'PERSONAL';
  const isLink = ( values?.categoria ?? 'ENLACE' ) === 'ENLACE';
  const isOportunidad = !isPersonal;

  useEffect( () => {
    if ( validateDocuments?.validacionId ) {
      form.change( 'validacionId', validateDocuments.validacionId );
    } else if ( validateDocuments?.estado === 'idle' ) {
      form.change( 'validacionId', null );
    }
  }, [form, validateDocuments?.validacionId, validateDocuments?.estado] );

  const toggleSearchModal = useCallback( ( type?: 'cc' | 'to' ) => {
    emailSearchType.current = type;
    setSearchModal( ( value ) => !value );
  }, [] );

  const setEmails = useCallback( ( newEmails?: string[] ) => {
    if ( emailSearchType.current === 'to' ) {
      const { emails } = form.getState().values;
      if ( newEmails && emails ) form.change( 'emails', Array.from( new Set( [...emails, ...newEmails] ) ) );
      else if ( newEmails ) form.change( 'emails', newEmails );
    } else if ( emailSearchType.current === 'cc' ) {
      const { emailsCc } = form.getState().values;
      if ( newEmails && emailsCc ) form.change( 'emailsCc', Array.from( new Set( [...emailsCc, ...newEmails] ) ) );
      else if ( newEmails ) form.change( 'emailsCc', newEmails );
    }
    toggleSearchModal();
  }, [] );

  return (
    <>
      <Row className="mt-2">
        <Col>
          <Select
            options={[
              { id: 'PERSONAL', name: 'PERSONAL' },
              { id: 'OPORTUNIDAD', name: 'OPORTUNIDAD' },
            ]}
            field="type"
            label="Tipo de documentación"
          />
        </Col>

        <Col>
          {isPersonal && (
          <Select
            options={[
              { id: 'EMAIL', name: 'EMAIL' },
              { id: 'ENLACE', name: 'ENLACE' },
            ]}
            field="categoria"
            label="Tipo de comunicación"
          />
          )}
          {isOportunidad && (
          <Select
            options={futureInvestments}
            field="oportunidadId"
            label="Oportunidad"
          />
          )}
        </Col>

      </Row>
      <Row className="mt-2">
        <Col md={isLink ? 12 : 6}>
          {isPersonal && (
            <>
              <Row className="gx-3 mt-2">
                <Col>
                  <Select
                    options={InvitationSenders.map( ( item ) => ( { id: item, name: item } ) )}
                    field="emailSender"
                    label="admin.invitations.sender"
                  />

                </Col>
                <Col>
                  <Datepicker
                    field="fechaExpiracionEnlace"
                    label="admin.invitations.expirationDate"
                    placeholder="dd/mm/yyyy"
                  />
                </Col>
              </Row>

              <Row className="">
                <Col>
                  <div className="form-group">
                    <FormCheck
                      type="switch"
                      className="switch-admin"
                      label="Nda Firmado"
                      checked={values?.firmaAutomiticaNda || false}
                      onChange={( e ) => form.change( 'firmaAutomiticaNda', e.target.checked )}
                    />
                  </div>
                </Col>
              </Row>

              <Card className="">
                <Card.Title className="p-2 text--sm">
                  <Row className="justify-content-between">
                    <Col xs="auto">
                      TO
                    </Col>
                    <Col xs="auto">
                      <Button size="sm" variant="link" className="p-0" onClick={() => toggleSearchModal( 'to' )}>
                        {t( 'admin.invitations.searchContacts' )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Title>
                <Card.Body className="px-2 pt-1">
                  <EmailListManager
                    form={form}
                    label={t( 'admin.invitations.emails' )}
                    field="email"
                    listField="emails"
                    errorField="emailError"
                    noItemsMessage={t( 'admin.invitations.noEmailsAdded' )}
                  />

                  {submitFailed && ( errors as Record<string, any> )?.emails && (
                  <div className="invalid-feedback d-block">{( errors as Record<string, any> ).emails}</div>
                  )}
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Title className="p-2 text--sm">
                  <Row className="justify-content-between">
                    <Col xs="auto">
                      CC
                    </Col>
                    <Col xs="auto">
                      <Button size="sm" variant="link" className="p-0" onClick={() => toggleSearchModal( 'cc' )}>
                        {t( 'admin.invitations.searchContacts' )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Title>
                <Card.Body className="px-2 pt-1">
                  <EmailListManager
                    form={form}
                    label={t( 'admin.invitations.emailsCc' )}
                    field="emailCc"
                    listField="emailsCc"
                    errorField="emailCcError"
                    noItemsMessage={t( 'admin.invitations.noemailsCcAdded' )}
                  />
                </Card.Body>
              </Card>
            </>
          )}

          {/* Árbol de documentos: siempre requerido */}

          <DocumentTreeSelector
            title="Carpetas de documentos"
            mode="folders"
            documentTree={documentTree}
            checked={values.carpetasPaths || []}
            expanded={expandedDocuments}
            onCheck={( checked ) => {
              form.change( 'carpetasPaths', checked );
              onDocumentsSelectionChange?.();
            }}
            onExpand={setExpandedDocuments}
            fieldState={form.getFieldState( 'carpetasPaths' )}
          />
          {submitFailed && ( errors as Record<string, any> )?.carpetasPaths && (
            <div className="invalid-feedback d-block">
              {( errors as Record<string, any> ).carpetasPaths}
            </div>
          )}
        </Col>

        {!isLink
        && (
        <Col md={6} className="mt-2">
          {isPersonal && (
          <>
            <TextField field="asuntoEmail" label="admin.invitations.subject" />
            <RswEditor
              containerProps={{ style: { resize: 'vertical', height: '100%' } }}
              placeholder="Plantilla de email"
              value={html}
              onChange={onChangeEditor}
            />
          </>
          )}
        </Col>
        )}
      </Row>

      {validateDocuments && (
        <ValidateDocumentsPanel
          estado={validateDocuments.estado}
          progreso={validateDocuments.progreso}
          total={validateDocuments.total}
          documentos={validateDocuments.documentos}
          resumen={validateDocuments.resumen}
          errorMessage={validateDocuments.errorMessage}
          puedeEnviar={validateDocuments.puedeEnviar}
          onValidate={validateDocuments.onValidate}
          translateDocStatus={validateDocuments.translateDocStatus}
          hasDocumentSelection={
            Array.isArray( values.carpetasPaths ) && values.carpetasPaths.length > 0
          }
        />
      )}

      <Row className="w-100 mt-4">
        <Col xs={6} className="m-auto">
          <Button
            variant="primary"
            type="submit"
            className="btn--full-width"
            disabled={
              submitting
              || validateDocuments?.estado === 'processing'
              || ( validateDocuments && !validateDocuments.puedeEnviar )
            }
          >
            {t( 'admin.common.create' )}
          </Button>
        </Col>
      </Row>

      {showSearchModal && (
      <SearchContactModal
        show
        onClose={toggleSearchModal}
        onSubmit={setEmails}
      />
      )}
    </>
  );
}

export default InvitationFormFields;
